import { useDispatch, useSelector } from 'react-redux';
import { customersAuthorizationApi } from '../api';
import { EndpointsMvp } from './enum';
import { useAuth } from './useAuth';
import { EToastTypes } from '../../store/ducks/toast/types';
import { addToast } from '../../store/ducks/toast/actions';

import {
  IProductDescriptionMVP,
  ProductDescription,
  ProductType,
  SearchProductsProps,
  SearchProductsResponse,
  SortProductsItem,
  SortProductsProps
} from './models';
import { ApplicationState } from '../../store';

import { useMemo } from 'react';
import { uppercaseOnlyFirstLetter } from '../../utils/formatStrings';
import { GetFlag } from '../../utils/featureFlag';
import { useShoppingCart } from '../../providers/shoppingcart.provider';

type ProductsPriceProps = 'all' | 'priced' | 'unpriced' | 'default';

export const useCatalogService = () => {
  const productsPriced = GetFlag('search_products_priced');
  const productsUnpriced = GetFlag('search_products_unpriced');

  const { updateProductsInformation } = useShoppingCart();

  const productsPriceFilter = (): ProductsPriceProps => {
    if (productsPriced && productsUnpriced) {
      return 'all';
    }

    if (productsPriced) {
      return 'priced';
    }

    if (productsUnpriced) {
      return 'unpriced';
    }

    return 'default';
  };

  const { getHeaders } = useAuth();
  const headers = getHeaders();

  const { emitterId, receiverId } = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const { customers } = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const customer = useMemo(
    () => customers.find((c) => c.customerId === emitterId),
    [emitterId]
  );

  const dispatch = useDispatch();

  const sortProducts = async ({
    sortType = 'R',
    currentPage = 1,
    pageSize = 12,
    productsSearch,
    options,
    paramsOptions = {
      withOptions: false,
      withPrice: true
    }
  }: SortProductsProps): Promise<{
    products: SortProductsItem[];
    total: number;
    hasError?: boolean;
  }> => {
    const receiver =
      customer &&
      customer?.purchaseReceivers &&
      customer?.purchaseReceivers.length > 0 &&
      receiverId.length === 0
        ? customer?.purchaseReceivers[0].code
        : receiverId;
    const bodyRequest = {
      soldTo: emitterId,
      shipTo: receiver
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const descriptionResponse = customer?.productsDescriptionResponse;
    const descriptionProducts = descriptionResponse?.products;
    const cod_setor_atividade = customer?.division;

    if (
      hasBodyKeyEmpty ||
      !descriptionProducts ||
      !Array.isArray(descriptionProducts) ||
      !Array.isArray(cod_setor_atividade)
    ) {
      return {
        products: [],
        total: 0
      };
    }

    const productsRelevance = descriptionProducts.map(
      (prd: IProductDescriptionMVP) => ({
        sku: String(prd.productGerdau),
        name: prd.productDescription,
        salesDate: prd.salesDate,
        internalCode: prd.productCustomer ?? '', // código do cliente
        productType: prd.productGPM
      })
    );

    const productsSearchFormatted = productsSearch?.map(
      (prd: ProductDescription) => ({
        ...prd,
        ...(sortType === 'AC' &&
          prd.expiration_date && { expireAgreementDate: prd.expiration_date })
      })
    );

    const products =
      sortType === 'R' ? productsRelevance : productsSearchFormatted;

    const optionsProductsType = options.filters.productsTypes ?? [];
    const productsTypeFilter = optionsProductsType.filter(
      (prd) => prd.length > 0
    );

    try {
      const params = {
        includeOptions: paramsOptions?.withOptions
          ? paramsOptions?.withOptions
          : false,
        includePrices: paramsOptions?.withPrice
          ? paramsOptions?.withPrice
          : false
      };

      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.PRODUCTS_SORT}`,
        {
          ...bodyRequest,
          sortType,
          currentPage: currentPage <= 0 ? 0 : currentPage - 1,
          pageSize,
          products,
          filter: {
            pricingType: productsPriceFilter(),
            input: options.filters.input,
            filterType:
              options.filters.materialCode === 'gerdauCode' ? 'CG' : 'CI', //CG: SKU | CI: Código interno
            ...(productsTypeFilter.length > 0 && {
              productTypes: productsTypeFilter
            }),
            setorAtividadeList: cod_setor_atividade.map((cod) => parseInt(cod))
          }
        },
        { headers, params }
      );

      if (response.data?.status === '500') {
        throw new Error();
      }

      if (
        response.data &&
        response.data.items &&
        Array.isArray(response.data.items)
      ) {
        //Update in memory products information (image/sap_descricao_gpm/descricao_gpm)
        updateProductsInformation(response.data.items);
      }

      return {
        products: response.data.items ?? response.data,
        total: response.data.totalItems ?? response.data.length
      };
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar itens',
          durationInMS: 3000
        })
      );
      return {
        products: [],
        total: 0,
        hasError: true
      };
    }
  };

  const productsTypes = async (): Promise<ProductType[]> => {
    const cod_setor_atividade = customer?.division;

    if (
      !emitterId ||
      !cod_setor_atividade ||
      !Array.isArray(cod_setor_atividade)
    ) {
      return [];
    }

    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.PRODUCTS_TYPES}`,
        {
          cod_setor_atividade: cod_setor_atividade.map((cod) => parseInt(cod))
        },
        { headers }
      );

      const data = response.data ?? [];

      const formattedTypes = data.map((type: ProductType) => ({
        ...type,
        description: uppercaseOnlyFirstLetter(type.description)
      }));

      return formattedTypes;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao indexar os tipos de produto',
          durationInMS: 3000
        })
      );
      return [];
    }
  };

  const searchProducts = async ({
    internalCode,
    sku,
    sap_descricao_gpm,
    searchType,
    currentPage = 1,
    pageSize = 12
  }: SearchProductsProps): Promise<{
    products: ProductDescription[];
    total: number;
  }> => {
    const receiver =
      customer &&
      customer?.purchaseReceivers &&
      customer?.purchaseReceivers.length > 0 &&
      receiverId.length === 0
        ? customer?.purchaseReceivers[0].code
        : receiverId;
    const bodyRequest = {
      soldTo: emitterId,
      shipTo: receiver
    };

    const cod_setor_atividade = customer?.division;
    const descriptionResponse = customer?.productsDescriptionResponse;
    const descriptionProducts = descriptionResponse?.products;

    if (
      !emitterId ||
      !cod_setor_atividade ||
      !Array.isArray(cod_setor_atividade) ||
      !descriptionProducts ||
      !Array.isArray(descriptionProducts)
    ) {
      return {
        products: [],
        total: 0
      };
    }

    try {
      const response =
        await customersAuthorizationApi.post<SearchProductsResponse>(
          `${EndpointsMvp.PRODUCTS_SEARCH}`,
          {
            ...bodyRequest,
            pricingType: productsPriceFilter(),
            customer_id: emitterId,
            internalCode: internalCode ? [internalCode] : [],
            sku: sku ? [sku.toLowerCase()] : [],
            ...(sap_descricao_gpm &&
              sap_descricao_gpm.length > 0 && { sap_descricao_gpm }),
            cod_setor_atividade: cod_setor_atividade.map((cod) =>
              parseInt(cod)
            ),
            ...(searchType && {
              searchType: 'AC'
            }),
            ...((!searchType || searchType !== 'AC') && {
              pageSize
            }),
            currentPage: currentPage <= 0 ? 0 : currentPage - 1
          },
          { headers }
        );

      const hits = response.data?.hits;
      const hitsProducts = hits?.hits ?? [];
      const total = hits?.total.value ?? 0;

      const formattedResponse: ProductDescription[] = hitsProducts.map(
        (prd) => ({
          sku: prd._source.sku,
          name: prd._source.name,
          productType: prd._source.sap_descricao_gpm ?? '',
          internalCode: prd._source.internal_code ?? '',
          expiration_date: prd._source.expiration_date
        })
      );

      return {
        products: formattedResponse,
        total
      };
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao filtrar itens',
          durationInMS: 3000
        })
      );
      return {
        products: [],
        total: 0
      };
    }
  };

  return {
    productsTypes,
    sortProducts,
    searchProducts
  };
};
